import React, { useEffect, useRef, useState } from "react";

import { cn } from "@src/lib/utils/cn";
import type { SceneObject } from "./afterimage/engine";
import type { OrbStateId } from "./afterimage/orb";
import {
  mountOrb,
  ORB_OVERSCAN,
  type OrbHandle,
  type OrbTexture,
  type OrbTone,
} from "./afterimage/orbStage";

type Props = {
  /** Rendered size in px. The orb is square. */
  size?: number;
  /** idle, thinking, working, needs (you), done or whirl (the spin). */
  state?: OrbStateId;
  /** The shape (an agent's bot); the round Ask Opus orb by default. */
  object?: SceneObject;
  /** graphite or pearl (white): an agent at rest; the Ask Opus fill when busy. */
  tone?: OrbTone;
  /** A graphite orb held in the Ask Opus fill even at idle (hovered). */
  warm?: boolean;
  /** One still frame per state, never animated (nav and header bots). */
  still?: boolean;
  /** pearl only: a colour token for the body ("--color-agent-orb"). */
  tint?: string;
  /** The Ask Opus orb in another gradient's two colours (from, to). */
  fill?: [string, string];
  /** A grainy photo texture on the fill. */
  texture?: OrbTexture;
  /** Hold the body still at rest; only its texture moves. */
  calm?: boolean;
  /** An edge that moves like a soft blob instead of a circle, 0..1. */
  wobble?: number;
  className?: string;
  /** Screen-reader label; omit on purely decorative instances. */
  label?: string;
};

/**
 * The afterimage orb: Ask Opus's soft blue-and-gold circle, or an agent's
 * bot outline (`object`, `tone="graphite"`), with eyes whose state
 * changes leave rainbow afterimages (src/features/assistantChat/afterimage).
 * All orbs share one WebGL2 canvas and one animation loop, paused while off
 * screen or in a hidden tab, and held still under reduced motion. Without
 * WebGL2 it shows a plain gradient disc.
 */
const AskOpusOrb: React.FC<Props> = ({
  size = 200,
  state = "idle",
  object,
  tone = "brand",
  warm = false,
  still = false,
  tint,
  fill,
  texture,
  calm,
  wobble,
  className,
  label,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Fill and texture are read at mount; a change of value (not a new
  // object with the same values) mounts a new orb.
  const fillKey = fill ? fill.join(" ") : "";
  const textureKey = texture ? JSON.stringify(texture) : "";
  const paintRef = useRef({ fill, texture });
  paintRef.current = { fill, texture };
  const orbRef = useRef<OrbHandle | null>(null);
  const [noGl, setNoGl] = useState(false);
  // A still spin would be a frozen mid-hop pose: show the done face instead.
  const shown = still && state === "whirl" ? "done" : state;
  // Read at mount only: later changes animate on the live orb.
  const stateRef = useRef(shown);
  stateRef.current = shown;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // If WebGL can't come back after a lost context, show the plain disc.
    const orb = mountOrb(
      canvas,
      size,
      stateRef.current,
      { object, tone, still, tint, calm, wobble, ...paintRef.current },
      () => setNoGl(true),
    );
    orbRef.current = orb;
    setNoGl(!orb);
    return () => {
      orb?.destroy();
      orbRef.current = null;
    };
    // fillKey and textureKey stand in for fill and texture (paintRef).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, object, tone, still, tint, fillKey, textureKey, calm, wobble]);

  useEffect(() => {
    orbRef.current?.set(shown);
  }, [shown]);

  useEffect(() => {
    orbRef.current?.setWarm(warm);
  }, [warm]);

  return (
    <div
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-orb-state={state}
      data-nogl={noGl ? "" : undefined}
      style={{ width: size, height: size }}
      className={cn("relative shrink-0", className)}
    >
      {/* Reaches past the box on every side so the spin's ribbon fits. */}
      <canvas
        ref={canvasRef}
        className={cn("pointer-events-none absolute block", noGl && "hidden")}
        style={{
          inset: `-${ORB_OVERSCAN * 100}%`,
          width: `${100 + 200 * ORB_OVERSCAN}%`,
          height: `${100 + 200 * ORB_OVERSCAN}%`,
        }}
      />
      {noGl && (
        <span
          data-orb-fallback
          // Its own colours when it has a fill (an agent), else the tone's.
          style={
            fill
              ? {
                  background: `radial-gradient(circle, ${fill[0]} 0%, ${fill[0]} 55%, ${fill[1]} 100%)`,
                }
              : undefined
          }
          className={cn(
            "absolute inset-[14%] rounded-full",
            fill
              ? "shadow-large"
              : tone === "pearl"
                ? "bg-(--color-agent-orb) shadow-large"
                : tone === "graphite"
                  ? "bg-[radial-gradient(circle,#5c5d66_0%,#33343b_70%,#1d1d22_100%)]"
                  : "bg-[radial-gradient(circle,#859CFD_0%,#ADBDFF_55%,#FFE276_100%)]",
          )}
        />
      )}
    </div>
  );
};

export default AskOpusOrb;
